// Passkeys in the browser without dependencies (docs/pwa/SPEC.md, docs/pwa/DESIGN.md §0).
// The server (laragear/webauthn) sends and expects binary fields as base64url strings.
import axios from 'axios';

const PASSKEY_ID_KEY = 'passkeyId';

export function isWebAuthnSupported() {
  return typeof window !== 'undefined'
    && typeof window.PublicKeyCredential === 'function'
    && typeof navigator.credentials?.get === 'function';
}

// Brand names do not decline; Russian words need a form per case (decision 26).
const brand = (name) => ({
  method: name, methodCap: name, methodAcc: name, methodIns: name, methodDat: name,
});

const WORDS = {
  ru: {
    android: {
      method: 'биометрия', methodCap: 'Биометрия', methodAcc: 'биометрию', methodIns: 'биометрией', methodDat: 'биометрии',
    },
    other: {
      method: 'ключ доступа', methodCap: 'Ключ доступа', methodAcc: 'ключ доступа', methodIns: 'ключом доступа', methodDat: 'ключу доступа',
    },
  },
  en: {
    android: { ...brand('biometrics'), methodCap: 'Biometrics' },
    other: { ...brand('passkey'), methodCap: 'Passkey' },
  },
};

/**
 * Name of the unlock method on this device as i18n params: {method}, {methodCap} (sentence start),
 * {methodAcc}, {methodIns}, {methodDat} (Russian cases). Decision 14: always "Face ID" on iOS.
 */
export function biometricWords(locale) {
  const platform = navigator.userAgentData?.platform ?? navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(platform)) {
    return brand('Face ID');
  }
  // iPadOS reports itself as a Mac with touch.
  if (/Mac/.test(platform)) {
    return brand(navigator.maxTouchPoints > 1 ? 'Face ID' : 'Touch ID');
  }
  if (/Win/.test(platform)) {
    return brand('Windows Hello');
  }
  const words = WORDS[locale] ?? WORDS.ru;
  return /Android/.test(platform) ? words.android : words.other;
}

function toBuffer(base64url) {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

function toBase64url(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function decodeDescriptors(list) {
  return (list ?? []).map((item) => ({ ...item, id: toBuffer(item.id) }));
}

export function storedPasskeyId() {
  try {
    return window.localStorage.getItem(PASSKEY_ID_KEY);
  } catch {
    return null;
  }
}

export function storePasskeyId(id) {
  try {
    if (id) {
      window.localStorage.setItem(PASSKEY_ID_KEY, id);
    } else {
      window.localStorage.removeItem(PASSKEY_ID_KEY);
    }
  } catch {
    // Storage blocked: Face ID is then only offered by a button, never called automatically.
  }
}

const FACE_ID_DECLINED_KEY = 'faceIdDeclined';

// "Not now" on the Face ID offer: never offered again on this device, only the settings (decision 21).
export function isFaceIdDeclined() {
  try {
    return window.localStorage.getItem(FACE_ID_DECLINED_KEY) === '1';
  } catch {
    return false;
  }
}

export function declineFaceId() {
  try {
    window.localStorage.setItem(FACE_ID_DECLINED_KEY, '1');
  } catch {
    // Storage blocked: the offer comes back after the next password sign-in.
  }
}

/**
 * Registers a passkey of this device for the signed-in user. Returns the server response data.
 */
export async function registerPasskey() {
  const { data: options } = await axios.post('/user/passkeys/options');
  const credential = await navigator.credentials.create({
    publicKey: {
      ...options,
      challenge: toBuffer(options.challenge),
      user: { ...options.user, id: toBuffer(options.user.id) },
      excludeCredentials: decodeDescriptors(options.excludeCredentials),
    },
  });

  const { data } = await axios.post('/user/passkeys', {
    id: credential.id,
    rawId: toBase64url(credential.rawId),
    type: credential.type,
    authenticatorAttachment: credential.authenticatorAttachment ?? undefined,
    clientExtensionResults: credential.getClientExtensionResults?.() ?? {},
    response: {
      clientDataJSON: toBase64url(credential.response.clientDataJSON),
      attestationObject: toBase64url(credential.response.attestationObject),
      transports: credential.response.getTransports?.() ?? [],
    },
  });
  storePasskeyId(data.credential_id);
  return data;
}

/**
 * Runs an assertion: optionsUrl gives the challenge, verifyUrl checks the signature
 * (/app-lock/passkey*: Face ID unlocks, it never signs in — decision 19).
 */
export async function assertPasskey(optionsUrl, verifyUrl) {
  const { data: options } = await axios.post(optionsUrl);
  const credential = await navigator.credentials.get({
    publicKey: {
      ...options,
      challenge: toBuffer(options.challenge),
      allowCredentials: decodeDescriptors(options.allowCredentials),
    },
  });

  const { data } = await axios.post(verifyUrl, {
    id: credential.id,
    rawId: toBase64url(credential.rawId),
    type: credential.type,
    authenticatorAttachment: credential.authenticatorAttachment ?? undefined,
    clientExtensionResults: credential.getClientExtensionResults?.() ?? {},
    response: {
      clientDataJSON: toBase64url(credential.response.clientDataJSON),
      authenticatorData: toBase64url(credential.response.authenticatorData),
      signature: toBase64url(credential.response.signature),
      userHandle: credential.response.userHandle ? toBase64url(credential.response.userHandle) : null,
    },
  });
  storePasskeyId(data.credential_id);
  return data;
}

/**
 * The user closed the system dialog (or Safari refused a call without a tap): not an error to show.
 */
export function isCancelled(error) {
  return error?.name === 'NotAllowedError' || error?.name === 'AbortError';
}
